import React from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Upload,
  FileText,
  Search,
  Download,
  Clock,
  Shield,
  User,
  Trash2,
  HelpCircle,
  CheckCircle,
  AlertTriangle,
  BookOpen
} from 'lucide-react';

const Section = ({ icon: Icon, title, children, color = "text-primary" }) => (
  <Card className="mb-6">
    <CardHeader className="pb-3">
      <CardTitle className="text-lg flex items-center gap-2">
        <Icon className={`w-5 h-5 ${color}`} />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="text-sm text-muted-foreground leading-relaxed space-y-3">
      {children}
    </CardContent>
  </Card>
);

const StudentGuide = () => {
  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="student-guide-page">
        <div>
          <h1 className="font-serif text-3xl font-bold text-foreground">Student User Guide</h1>
          <p className="text-muted-foreground mt-1">How to use the Riverbank Academy Plagiarism Checker</p>
        </div>

        <Section icon={BookOpen} title="Getting Started">
          <p>Welcome to the Riverbank Academy Plagiarism Checker. This tool helps you check your academic documents for similarity against web sources, academic journals, and an internal repository before submission.</p>
          <p>After registering, your account requires <strong>administrator approval</strong>. You will receive an email at your registered address once approved.</p>
        </Section>

        <Section icon={Upload} title="Uploading a Document" color="text-blue-600">
          <p>To check a document for plagiarism:</p>
          <ol className="list-decimal list-inside space-y-2 ml-2">
            <li>Click <strong>"Upload"</strong> in the sidebar navigation</li>
            <li>Enter a <strong>title</strong> for your submission (e.g., "History Essay Draft 2")</li>
            <li>Drag and drop your file or click to browse</li>
            <li>Click <strong>"Upload & Scan"</strong> to begin the analysis</li>
          </ol>
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 mt-2">
            <p className="text-blue-800"><strong>Supported formats:</strong> PDF, DOCX, DOC, ODT, RTF, TXT</p>
            <p className="text-blue-800"><strong>Maximum file size:</strong> 100 MB</p>
          </div>
        </Section>

        <Section icon={Clock} title="Scanning Process" color="text-amber-600">
          <p>Once uploaded, your document goes through multiple checks:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>Web Source Check</strong> — compares against internet sources</li>
            <li><strong>Academic Journal Check</strong> — searches Semantic Scholar, CrossRef, and OpenAlex</li>
            <li><strong>Internal Repository</strong> — checks against previously submitted documents at Riverbank Academy</li>
            <li><strong>AI Content Detection</strong> — analyses if content was generated by AI</li>
          </ul>
          <p>Scanning typically takes <strong>1-3 minutes</strong> depending on document length.</p>
        </Section>

        <Section icon={Search} title="Understanding Your Results" color="text-emerald-600">
          <p>After scanning, you'll see a <strong>Similarity Score</strong> and <strong>Risk Level</strong>:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3">
              <p className="font-semibold text-emerald-800">Low Risk (0-15%)</p>
              <p className="text-emerald-700 text-xs">Minimal similarity detected. Your work appears original.</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
              <p className="font-semibold text-amber-800">Moderate Risk (15-30%)</p>
              <p className="text-amber-700 text-xs">Some similarity found. Review flagged sections.</p>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-sm p-3">
              <p className="font-semibold text-orange-800">High Risk (30-50%)</p>
              <p className="text-orange-700 text-xs">Significant similarity. Revise before final submission.</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-sm p-3">
              <p className="font-semibold text-red-800">Critical (50%+)</p>
              <p className="text-red-700 text-xs">Extensive similarity detected. Substantial revision needed.</p>
            </div>
          </div>
        </Section>

        <Section icon={Download} title="Downloading Reports" color="text-purple-600">
          <p>For each completed scan, you can:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>View Report in Browser</strong> — opens a detailed HTML report</li>
            <li><strong>Download PDF</strong> — saves a PDF copy of the report</li>
            <li><strong>Download HTML</strong> — saves the report as an HTML file</li>
          </ul>
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mt-2">
            <p className="text-amber-800"><strong>Mac/Safari users:</strong> If the PDF download doesn't work, use "View in Browser" and press Cmd+P to save as PDF.</p>
          </div>
        </Section>

        <Section icon={FileText} title="Submission History" color="text-foreground">
          <p>Click <strong>"History"</strong> in the sidebar to see all your past submissions. You can:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>View the status of each submission</li>
            <li>Click any submission to see detailed results</li>
            <li>Re-download reports from previous scans</li>
          </ul>
        </Section>

        <Section icon={User} title="Account Management" color="text-foreground">
          <p>Click <strong>"Account"</strong> in the sidebar to manage your data:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>Export My Data</strong> — download all your data as a JSON file</li>
            <li><strong>Delete Submissions</strong> — remove individual or all submissions</li>
            <li><strong>Delete Account</strong> — permanently remove your account and all data</li>
          </ul>
        </Section>

        <Section icon={HelpCircle} title="Tips for Best Results" color="text-blue-600">
          <ul className="list-disc list-inside space-y-2 ml-2">
            <li>Upload your document <strong>before</strong> final submission to your lecturer</li>
            <li>Use proper citations and references to reduce similarity scores</li>
            <li>Direct quotes should be enclosed in quotation marks</li>
            <li>Paraphrase information in your own words rather than copying</li>
            <li>Include a bibliography — it helps distinguish referenced content from plagiarism</li>
          </ul>
        </Section>

        <Section icon={AlertTriangle} title="Important Notes" color="text-red-600">
          <ul className="list-disc list-inside space-y-2 ml-2">
            <li>A high similarity score does <strong>not automatically mean plagiarism</strong> — properly cited references may also be flagged</li>
            <li>All uploaded documents are stored securely and may be added to the internal repository for future checks</li>
            <li>Contact the Academic Integrity Office if you have concerns about your results</li>
          </ul>
        </Section>
      </div>
    </Layout>
  );
};

export default StudentGuide;
